import { LicensePreviewDTO, Nullable, TeamInvoiceDTO } from "@shaarp/common";

type Translate = (key: string, options?: Record<string, unknown>) => string;

type LicenseUpdateToastInput = {
  /** Total the team holds after the change. */
  total: number;
  /** Seats it held before. */
  purchased: number;
  preview: Nullable<LicensePreviewDTO>;
  currentPeriodEndsAt: Nullable<Date>;
  /** The invoice the change issued, for a team without a payment method. */
  issuedInvoice: Nullable<TeamInvoiceDTO>;
  /** The team is invoiced instead of charged (no card, exempted). */
  paidByInvoice: boolean;
  t: Translate;
  formatAmount: (amount: number, currency: string) => string;
  formatDate: (date: Nullable<Date>) => string;
};

const PREFIX = "team.licenseManagement.dialog.toast";

/**
 * Success toast spelling out what actually happened — the outcome differs a
 * lot between an immediate purchase, an invoice to pay by transfer (#742) and a
 * decrease that only lands at the next renewal, so a bare "Abonnements" title
 * tells the manager nothing.
 */
export const buildLicenseUpdateToast = ({
  total,
  purchased,
  preview,
  currentPeriodEndsAt,
  issuedInvoice,
  paidByInvoice,
  t,
  formatAmount,
  formatDate,
}: LicenseUpdateToastInput): { title: string; description: string } => {
  if (preview?.isScheduledChange) {
    const effectiveAt = formatDate(
      preview.scheduledEffectiveAt ?? currentPeriodEndsAt
    );
    return {
      title: t(`${PREFIX}.scheduledTitle`),
      description: effectiveAt
        ? t(`${PREFIX}.scheduledDescription`, { total, date: effectiveAt })
        : t("team.licenseManagement.dialog.licensesActiveUntilRenewal"),
    };
  }

  if (issuedInvoice?.dueAt) {
    return {
      title: t(`${PREFIX}.invoiceTitle`),
      description: t(`${PREFIX}.invoiceDescription`, {
        amount: formatAmount(issuedInvoice.amountDue, issuedInvoice.currency),
        date: formatDate(issuedInvoice.dueAt),
        total,
      }),
    };
  }

  const addedSeats = Math.max(0, total - purchased);
  // Nothing was charged without a card: either the invoice could not be
  // issued at once and Stripe sends it within the hour, or there was nothing
  // to bill now (a trial).
  if (addedSeats > 0 && paidByInvoice) {
    return (preview?.immediateChargeInclTax ?? 0) > 0
      ? {
          title: t(`${PREFIX}.increaseTitle`, { count: addedSeats }),
          description: t(`${PREFIX}.invoiceLaterDescription`, { total }),
        }
      : {
          title: t(`${PREFIX}.updatedTitle`),
          description: t(`${PREFIX}.updatedDescription`, { total }),
        };
  }

  if (addedSeats > 0 && preview) {
    return {
      title: t(`${PREFIX}.increaseTitle`, { count: addedSeats }),
      description: t(`${PREFIX}.increaseDescription`, {
        amount: formatAmount(preview.immediateChargeInclTax, preview.currency),
        total,
      }),
    };
  }

  return {
    title: t(`${PREFIX}.updatedTitle`),
    description: t(`${PREFIX}.updatedDescription`, { total }),
  };
};
